import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react'
import Joyride, { CallBackProps, STATUS, EVENTS, ACTIONS } from 'react-joyride'
import { useNavigate, useLocation } from 'react-router-dom'
import { apiClient } from '@/services/api'
import { checkoutService } from '@/services/checkout'
import { useAuthStore } from '@/store/authStore'
import { useLessonStore } from '@/store/lessonStore'
import { DEFAULT_ORG_ID } from '@/constants'
import { TOURS, TOUR_ORDER, type TourId, type TourStep } from './tourSteps'
import type { Course } from '@/types'

const TOURS_COMPLETED_KEY = 'captain_tours_completed'
const OLD_TOUR_KEY = 'captain_tour_completed'

interface TourContextValue {
  startTour: (tourId: TourId) => void
  activeTourId: TourId | null
  isTourActive: boolean
  completedTours: string[]
  skippedTours: string[]
  isTourCompleted: boolean // backward compat: true if getting_started is done
}

const TourContext = createContext<TourContextValue>({
  startTour: () => {},
  activeTourId: null,
  isTourActive: false,
  completedTours: [],
  skippedTours: [],
  isTourCompleted: false,
})

export function useTour() {
  return useContext(TourContext)
}

// Poll for a target element to appear in the DOM (up to maxWait ms)
function waitForTarget(selector: string, maxWait = 3000): Promise<boolean> {
  return new Promise((resolve) => {
    // 'body' is always present
    if (selector === 'body') { resolve(true); return }
    const start = Date.now()
    const check = () => {
      if (document.querySelector(selector)) { resolve(true); return }
      if (Date.now() - start > maxWait) { resolve(false); return }
      requestAnimationFrame(check)
    }
    // Give navigation a moment to start rendering
    setTimeout(check, 300)
  })
}

export function TourProvider({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate()
  const location = useLocation()
  const user = useAuthStore((s) => s.user)
  const [run, setRun] = useState(false)
  const [stepIndex, setStepIndex] = useState(0)
  const [steps, setSteps] = useState<TourStep[]>([])
  const [activeTourId, setActiveTourId] = useState<TourId | null>(null)
  const isNavigatingRef = useRef(false)
  const demoCourseRef = useRef<Course | null>(null)
  const activeTourIdRef = useRef<TourId | null>(null)
  const completingRef = useRef(false)

  const completedTours = user?.tours_completed ?? []
  const skippedTours = user?.tours_skipped ?? []

  // Migrate old localStorage key on mount
  useEffect(() => {
    const oldValue = localStorage.getItem(OLD_TOUR_KEY)
    if (oldValue === 'true' && user && !completedTours.includes('getting_started')) {
      apiClient.completeTour('getting_started').then((res) => {
        if (res.data) {
          const freshUser = useAuthStore.getState().user
          if (freshUser) {
            useAuthStore.getState().setUser({ ...freshUser, tours_completed: res.data.tours_completed, credit_balance: res.data.credit_balance })
          }
        }
      }).catch(() => {})
      localStorage.removeItem(OLD_TOUR_KEY)
    } else if (oldValue) {
      localStorage.removeItem(OLD_TOUR_KEY)
    }
  }, [user?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  const startTour = useCallback(async (tourId: TourId) => {
    const tourConfig = TOURS[tourId]
    if (!tourConfig) return

    let tourSteps = [...tourConfig.steps]

    // Fetch demo course for dynamic routing
    try {
      const response = await apiClient.getCourses(DEFAULT_ORG_ID)
      const courses = (response.data as any) || []
      const demoCourse = courses.find((c: Course) => c.source_course_id != null)
      demoCourseRef.current = demoCourse || null

      if (demoCourse) {
        const courseDetailRoute = `/organizations/${DEFAULT_ORG_ID}/courses/${demoCourse.id}`

        if (tourId === 'getting_started') {
          // Update step 5 (Create a Run) with dynamic route
          tourSteps[5] = { ...tourSteps[5], route: courseDetailRoute }
        } else if (tourId === 'live_class') {
          // Find a run with draft lessons, or create one so the user can start a lesson
          try {
            const runsRes = await apiClient.getMyRuns(DEFAULT_ORG_ID, demoCourse.id)
            const runs = (runsRes.data as any) || []

            // Prefer a run with a draft lesson (startable)
            let targetLesson: any = null
            for (const r of runs) {
              const draft = (r.lessons || []).find((l: any) => l.status === 'draft' || l.status === 'ready')
              if (draft) { targetLesson = draft; break }
            }

            // No draft lesson found — create a new run
            if (!targetLesson) {
              try {
                const newRunRes = await apiClient.createCourseRun(DEFAULT_ORG_ID, demoCourse.id, 'Onboarding Run')
                const newRun = newRunRes.data as any
                if (newRun?.lessons?.[0]) {
                  targetLesson = newRun.lessons[0]
                }
              } catch {
                // If run creation fails, fall back to any lesson
                const anyRun = runs.find((r: any) => r.lessons?.length > 0)
                if (anyRun?.lessons?.[0]) targetLesson = anyRun.lessons[0]
              }
            }

            if (targetLesson?.id) {
              // Check if payment method is needed for paid courses
              if ((demoCourse.credit_cost ?? 0) > 0) {
                try {
                  const balance = await checkoutService.getBalance()
                  if (!balance.has_payment_method) {
                    // Insert a payment step before the start-lesson step
                    tourSteps.splice(1, 0, {
                      target: 'body',
                      title: 'Add a Payment Method',
                      content: 'This course requires a payment method on file. Let\'s set that up first.',
                      placement: 'center',
                      disableBeacon: true,
                      route: `/add-payment-method`,
                    })
                  }
                } catch { /* continue without payment check */ }
              }

              // Set route on the start-lesson step (may have shifted if payment step was inserted)
              const startStepIdx = tourSteps.findIndex(s => (s.target as string)?.includes('start-lesson-btn'))
              if (startStepIdx >= 0) {
                tourSteps[startStepIdx] = { ...tourSteps[startStepIdx], route: `/lesson/${targetLesson.id}?host=true` }
              }
            }
          } catch {
            // Continue without dynamic routing
          }
        } else if (tourId === 'reports') {
          // Navigate through report pages
          tourSteps[1] = { ...tourSteps[1], route: courseDetailRoute }

          const courseReportRoute = `/organizations/${DEFAULT_ORG_ID}/courses/${demoCourse.id}/report`
          tourSteps[2] = { ...tourSteps[2], route: courseReportRoute }
          tourSteps[3] = { ...tourSteps[3], route: `${courseReportRoute}?tab=runs` }

          try {
            const runsRes = await apiClient.getMyRuns(DEFAULT_ORG_ID, demoCourse.id)
            const runs = (runsRes.data as any) || []
            if (runs.length > 0) {
              // Prefer a run that has completed/ended lessons (better report data)
              const run = runs.find((r: any) =>
                (r.lessons || []).some((l: any) => l.status === 'completed' || l.status === 'ended')
              ) || runs[0]

              const runReportRoute = `/organizations/${DEFAULT_ORG_ID}/courses/${demoCourse.id}/runs/${run.id}/report`
              tourSteps[4] = { ...tourSteps[4], route: runReportRoute }

              // Prefer a completed/ended lesson for the lesson report
              const reportLesson = (run.lessons || []).find((l: any) => l.status === 'completed' || l.status === 'ended')
              if (reportLesson?.id) {
                const lessonReportRoute = `/lessons/${reportLesson.id}/report`
                tourSteps[5] = { ...tourSteps[5], route: lessonReportRoute }
                // Set export step route to the same page so it can find the target
                tourSteps[6] = { ...tourSteps[6], route: lessonReportRoute }
              } else {
                // No completed lesson — remove lesson report + export steps to avoid blank page
                tourSteps.splice(5, 2)
              }
            }
          } catch {
            // Continue without dynamic routing
          }
        }
      }
    } catch {
      // Continue tour without dynamic routing
    }

    setSteps(tourSteps)
    setActiveTourId(tourId)
    activeTourIdRef.current = tourId
    completingRef.current = false
    setStepIndex(0)
    setRun(true)
  }, [])

  const completeTour = useCallback(async (tourId: TourId) => {
    // Guard: prevent double-completion from multiple callback paths
    if (completingRef.current) return
    completingRef.current = true

    const currentUser = useAuthStore.getState().user
    if (!currentUser) { completingRef.current = false; return }

    try {
      const res = await apiClient.completeTour(tourId)
      if (res.data) {
        // Read fresh user from store to avoid stale spread
        const freshUser = useAuthStore.getState().user
        if (freshUser) {
          useAuthStore.getState().setUser({
            ...freshUser,
            tours_completed: res.data.tours_completed,
            tours_skipped: res.data.tours_skipped,
            credit_balance: res.data.credit_balance,
          })
        }
        localStorage.setItem(TOURS_COMPLETED_KEY, JSON.stringify(res.data.tours_completed))
      }
    } catch (err) {
      console.error('Failed to complete tour:', tourId, err)
    }
  }, [])

  const skipTourApi = useCallback(async (tourId: TourId) => {
    try {
      const res = await apiClient.skipTour(tourId)
      if (res.data) {
        const freshUser = useAuthStore.getState().user
        if (freshUser) {
          useAuthStore.getState().setUser({
            ...freshUser,
            tours_skipped: res.data.tours_skipped,
            credit_balance: res.data.credit_balance,
          })
        }
      }
    } catch (err) {
      console.error('Failed to skip tour:', tourId, err)
    }
  }, [])

  const finishTour = useCallback((skipped = false) => {
    setRun(false)
    setStepIndex(0)
    const tourId = activeTourIdRef.current
    if (tourId) {
      if (skipped) {
        skipTourApi(tourId)
      } else {
        completeTour(tourId)
      }
    }
    setActiveTourId(null)
    activeTourIdRef.current = null
  }, [completeTour, skipTourApi])

  const handleJoyrideCallback = useCallback(
    (data: CallBackProps) => {
      const { status, action, index, type } = data

      // Tour finished or skipped
      if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
        finishTour(status === STATUS.SKIPPED)
        return
      }

      if (type === EVENTS.STEP_AFTER || type === EVENTS.TARGET_NOT_FOUND) {
        const direction = action === ACTIONS.PREV ? -1 : 1
        let nextIndex = index + direction

        // Skip steps whose targets can't be found (e.g. no runs/lessons exist)
        const findNextValidStep = async (startIdx: number): Promise<number> => {
          let idx = startIdx
          while (idx >= 0 && idx < steps.length) {
            const step = steps[idx]
            const selector = typeof step.target === 'string' ? step.target : 'body'

            // Navigate if needed
            if (step.route && location.pathname + location.search !== step.route) {
              isNavigatingRef.current = true
              setRun(false)
              navigate(step.route)
              const found = await waitForTarget(selector, 5000)
              isNavigatingRef.current = false
              if (found) return idx
            } else {
              // Already on the right page — check if target exists or is 'body'
              if (selector === 'body' || document.querySelector(selector)) return idx
              // Give it a moment in case component is still rendering
              const found = await waitForTarget(selector, 2000)
              if (found) return idx
            }
            idx += direction
          }
          return -1 // No valid step found
        }

        findNextValidStep(nextIndex).then((validIndex) => {
          if (validIndex < 0 || validIndex >= steps.length) {
            finishTour()
          } else {
            setStepIndex(validIndex)
            setRun(true)
          }
        })
      }
    },
    [steps, location.pathname, location.search, navigate, finishTour]
  )

  // Auto-advance past the "Click Start" step when lesson goes live
  const lessonStatus = useLessonStore((s) => s.currentLesson?.status)
  const stepIndexRef = useRef(0)
  stepIndexRef.current = stepIndex

  useEffect(() => {
    if (
      activeTourIdRef.current === 'live_class' &&
      (lessonStatus === 'live' || lessonStatus === 'running') &&
      steps[stepIndexRef.current] &&
      (steps[stepIndexRef.current].target as string)?.includes('start-lesson-btn')
    ) {
      // Lesson just went live — wait for the host dashboard to render, then advance
      setRun(false)
      setTimeout(() => {
        setStepIndex(stepIndexRef.current + 1)
        setRun(true)
      }, 800)
    }
  }, [lessonStatus, steps])

  const isTourCompleted = completedTours.includes('getting_started')

  return (
    <TourContext.Provider value={{ startTour, activeTourId, isTourActive: run, completedTours, skippedTours, isTourCompleted }}>
      {children}
      <Joyride
        steps={steps}
        run={run}
        stepIndex={stepIndex}
        continuous
        showSkipButton
        showProgress
        callback={handleJoyrideCallback}
        disableOverlayClose
        hideCloseButton
        hideBackButton
        spotlightClicks
        locale={{
          close: 'Close',
          last: 'Finish',
          next: 'Next',
          skip: 'Skip Tour',
        }}
        floaterProps={{
          styles: {
            arrow: {
              color: '#0f1d32',
              length: 8,
              spread: 16,
            },
            floater: {
              filter: 'drop-shadow(0 0 0.5px rgba(0, 255, 136, 0.35)) drop-shadow(0 0 0.5px rgba(0, 255, 136, 0.35))',
            },
          },
        }}
        styles={{
          options: {
            zIndex: 10000,
            primaryColor: '#00ff88',
            backgroundColor: '#0f1d32',
            textColor: '#e0e0e0',
            arrowColor: '#0f1d32',
            overlayColor: 'rgba(0, 0, 0, 0.6)',
          },
          tooltip: {
            borderRadius: '12px',
            border: '1px solid rgba(0, 255, 136, 0.2)',
            fontFamily: '"Roboto Mono", monospace',
          },
          tooltipTitle: {
            fontFamily: '"Orbitron", sans-serif',
            fontSize: '16px',
            color: '#00ff88',
          },
          tooltipContent: {
            fontSize: '13px',
            lineHeight: '1.6',
          },
          buttonNext: {
            backgroundColor: '#00ff88',
            color: '#0a1628',
            fontFamily: '"Roboto Mono", monospace',
            fontWeight: 600,
            borderRadius: '8px',
            fontSize: '13px',
          },
          buttonBack: {
            color: '#00ff88',
            fontFamily: '"Roboto Mono", monospace',
            fontSize: '13px',
          },
          buttonSkip: {
            color: '#666',
            fontFamily: '"Roboto Mono", monospace',
            fontSize: '12px',
          },
          spotlight: {
            borderRadius: '12px',
          },
        }}
      />
    </TourContext.Provider>
  )
}
